import { CSSProperties, ComponentPropsWithoutRef, ReactNode, forwardRef } from 'react';

import { styled } from '../theme';
import SvgIcon from './SvgIcon';
import Text from './Text';
import Tooltip from './Tooltip';

type Props = ComponentPropsWithoutRef<'a'> & {
    beforeIconSrc?: string;
    beforeIconAlt?: string;
    disabled?: boolean;
    centered?: boolean;
    checkIsActive?: () => boolean;
    styles?: CSSProperties;
    tooltip?: ReactNode;
    tooltipDescription?: ReactNode;
};

type SideMenuLinkProps = {
    disabled?: boolean;
    isActive?: boolean;
    styles?: CSSProperties;
};

const StyledSvgIcon = styled(SvgIcon)({
    color: 'inherit',
});

const SideMenuLink = styled(Text.withComponent('a'))<SideMenuLinkProps>(
    ({ theme, disabled, isActive, styles }) => ({
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        minHeight: 40,
        fontWeight: theme.typography.fontWeight.roman,
        ':hover, :focus': {
            color: theme.color.primary1,
            svg: {
                color: theme.color.primary1,
            },
            textDecoration: 'none',
        },
        ':active, &.active': {
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.color.primary1,
            svg: {
                color: theme.color.primary1,
            },
        },
        ...(disabled && {
            color: theme.color.gray70,
            cursor: 'default',
            pointerEvents: 'none',
        }),
        '* + *': {
            marginLeft: 15,
        },
        ...(isActive && {
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.color.primary1,
            svg: {
                color: theme.color.primary1,
            },
        }),
        ...(styles && styles),
    })
);

const Description = styled.div(({ theme }) => ({
    color: theme.color.gray70,
    marginTop: 4,
}));

export default forwardRef<HTMLAnchorElement, Props>(
    (
        {
            children,
            beforeIconSrc,
            beforeIconAlt,
            checkIsActive,
            tooltip,
            tooltipDescription,
            ...props
        },
        ref
    ) => {
        const content = (
            <SideMenuLink ref={ref} {...props} isActive={checkIsActive?.()}>
                {beforeIconSrc && (
                    <StyledSvgIcon alt={beforeIconAlt || ''} src={beforeIconSrc} size="lg" />
                )}
                {children}
            </SideMenuLink>
        );

        if (tooltip != null) {
            const tooltipContent = (
                <div>
                    <div>{tooltip}</div>
                    {tooltipDescription != null && <Description>{tooltipDescription}</Description>}
                </div>
            );

            return (
                <Tooltip title={tooltipContent} placement="right">
                    {content}
                </Tooltip>
            );
        }

        return content;
    }
);
